import "@/index.css";
import "@/lib/i18n"; // Initialize i18n before anything else
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OperationsProvider } from "@/contexts/OperationsContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

// ==================== LAZY-LOADED PAGES ====================
// Reduces initial bundle size — each page loaded on demand
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const CreatorPage = lazy(() => import("@/pages/CreatorPage"));
const StudioPage = lazy(() => import("@/pages/StudioPage"));
const SourcesPage = lazy(() => import("@/pages/SourcesPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const PdfLabPage = lazy(() => import("@/pages/PdfLabPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const LegalSearchPage = lazy(() => import("@/pages/LegalSearchPage"));
const KnowledgeBasePage = lazy(() => import("@/pages/KnowledgeBasePage"));
const ContractBenchmarkPage = lazy(() => import("@/pages/ContractBenchmarkPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const AgentPage = lazy(() => import("@/pages/AgentPage"));
const CalculatorsPage = lazy(() => import("@/pages/CalculatorsPage"));
const PredictionPage = lazy(() => import("@/pages/PredictionPage"));
const ContractAnalysisPage = lazy(() => import("@/pages/ContractAnalysisPage"));
const CourtPortalPage = lazy(() => import("@/pages/CourtPortalPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const CompliancePage = lazy(() => import("@/pages/CompliancePage"));
const DueDiligencePage = lazy(() => import("@/pages/DueDiligencePage"));
const DetectivePage = lazy(() => import("@/pages/DetectivePage"));
const DeveloperPage = lazy(() => import("@/pages/DeveloperPage"));
const NegotiationPage = lazy(() => import("@/pages/NegotiationPage"));
const KnowledgeGraphPage = lazy(() => import("@/pages/KnowledgeGraphPage"));
const TrashPage = lazy(() => import("@/pages/TrashPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminInboxPage = lazy(() => import("@/pages/AdminInboxPage"));
const AdminReportsPage = lazy(() => import("@/pages/AdminReportsPage"));
const MesSignalementsPage = lazy(() => import("@/pages/MesSignalementsPage"));
const GlobalHistoryPage = lazy(() => import("@/pages/GlobalHistoryPage"));
const UserInboxPage = lazy(() => import("@/pages/UserInboxPage"));
const AdvicePage = lazy(() => import("@/pages/AdvicePage"));
const SuspendedPage = lazy(() => import("@/pages/SuspendedPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
// Legal pages (RGPD, LCEN, CNIL compliance)
const MentionsLegalesPage = lazy(() => import("@/pages/legal/MentionsLegalesPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const CGUPage = lazy(() => import("@/pages/legal/CGUPage"));
const CGVPage = lazy(() => import("@/pages/legal/CGVPage"));
const CookiesPage = lazy(() => import("@/pages/legal/CookiesPage"));
// Error pages
const NotFoundPage = lazy(() => import("@/pages/errors/NotFoundPage"));

// Non-lazy (needed immediately for layout)
import AppShell from "@/components/AppShell";
import GlobalSearch from "@/components/GlobalSearch";
import OnboardingWizard from "@/components/OnboardingWizard";
import CookieConsent from "@/components/CookieConsent";

// ==================== LOADING FALLBACK ====================
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function ProtectedRoute({ children, allowSuspended = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <PageLoader />;
  }
  if (!user) return <Navigate to="/login" replace />;
  // Strict === false check: undefined (existing users without field) passes through
  if (user.email_verified === false) {
    return <Navigate to="/verify-email" replace />;
  }
  if (!allowSuspended && user.account_status === "suspended") {
    return <Navigate to="/suspended" replace />;
  }
  return children;
}

function RoleProtectedRoute({ children, allowedRoles, allowSuspended = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <PageLoader />;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified === false) {
    return <Navigate to="/verify-email" replace />;
  }
  if (!allowSuspended && user.account_status === "suspended") {
    return <Navigate to="/suspended" replace />;
  }
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public pages */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/google/callback" element={<LoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />

          {/* Suspended account page */}
          <Route path="/suspended" element={
            <ProtectedRoute allowSuspended><SuspendedPage /></ProtectedRoute>
          } />

          {/* Settings — accessible when suspended (to upgrade plan) */}
          <Route element={<ProtectedRoute allowSuspended><AppShell /></ProtectedRoute>}>
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Protected — Dashboard + Tools (blocked when suspended) */}
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/legal-search" element={<LegalSearchPage />} />
            <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
            <Route path="/benchmark" element={<ContractBenchmarkPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/calculators" element={<CalculatorsPage />} />
            <Route path="/prediction" element={<PredictionPage />} />
            <Route path="/contract-analysis" element={<ContractAnalysisPage />} />
            <Route path="/court-portal" element={<CourtPortalPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/due-diligence" element={<DueDiligencePage />} />
            <Route path="/due-diligence/:ddId" element={<DueDiligencePage />} />
            <Route path="/detective" element={<DetectivePage />} />
            <Route path="/negotiation" element={<NegotiationPage />} />
            <Route path="/knowledge-graph" element={<KnowledgeGraphPage />} />
            <Route path="/trash" element={<TrashPage />} />
            <Route path="/inbox" element={<UserInboxPage />} />
            <Route path="/advice" element={<AdvicePage />} />
            <Route path="/history" element={<GlobalHistoryPage />} />
            <Route path="/mes-signalements" element={<MesSignalementsPage />} />
          </Route>

          {/* Admin — owner + admin only */}
          <Route element={<RoleProtectedRoute allowedRoles={["owner", "admin"]}><AppShell /></RoleProtectedRoute>}>
            <Route path="/developer" element={<DeveloperPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/reports" element={<AdminReportsPage />} />
          </Route>

          {/* Owner only */}
          <Route element={<RoleProtectedRoute allowedRoles={["owner"]}><AppShell /></RoleProtectedRoute>}>
            <Route path="/admin/inbox" element={<AdminInboxPage />} />
          </Route>

          {/* Protected — Project views */}
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route path="/project/:projectId/creator" element={<CreatorPage />} />
            <Route path="/project/:projectId/studio" element={<StudioPage />} />
            <Route path="/project/:projectId/sources" element={<SourcesPage />} />
            <Route path="/project/:projectId/history" element={<HistoryPage />} />
            <Route path="/project/:projectId/pdf-lab" element={<PdfLabPage />} />
            <Route path="/project/:projectId/agent" element={<AgentPage />} />
            <Route path="/project/:projectId" element={<Navigate to="creator" replace />} />
          </Route>

          {/* Legal pages (RGPD, LCEN — publicly accessible, no auth) */}
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/politique-confidentialite" element={<PrivacyPolicyPage />} />
          <Route path="/cgu" element={<CGUPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/cookies" element={<CookiesPage />} />

          {/* 404 — explicit not-found page (improves SEO + UX vs silent redirect) */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {/* Global overlays */}
      <GlobalSearch />
      <OnboardingWizard />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <OperationsProvider>
            <AppRoutes />
            <Toaster position="bottom-right" richColors />
          </OperationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
